import { Navigate, useNavigate } from 'react-router';
import { clsx } from '../ds/clsx';
import { Avatar, Eyebrow, TextLink } from '../ds/components';
import { useIsMobile } from '../ds/hooks';
import { useSession, useWorkspace } from '../lib/session';
import { useShellActions } from './ShellContext';
import { moreSections } from './navigation';
import styles from './MorePage.module.css';

/**
 * "Mehr" (Shell 10, second mockup; regrouped D-314): „Tasks“ first, then the areas „Kunden“, „Immobilien“,
 * „Auswertung“ and „Werkzeuge“ with small eyebrows, the profile card.
 */
export function MorePage() {
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const session = useSession();
  const workspace = useWorkspace();
  const actions = useShellActions();

  if (!mobile) return <Navigate to="/home" replace />;

  const sections = moreSections({
    tools: workspace.tools,
    permissions: workspace.permissions,
    modules: workspace.modules,
  });
  const name = `${workspace.user.firstName} ${workspace.user.lastName}`.trim();
  const multiWorkspace = session.workspaces.length > 1;

  return (
    <div className={styles.page}>
      {sections.map((section) => (
        <section
          key={section.eyebrow ?? 'tasks'}
          className={styles.section}
          aria-label={section.eyebrow ?? undefined}
        >
          {section.eyebrow ? (
            <Eyebrow as="div" className={styles.eyebrow}>
              {section.eyebrow}
            </Eyebrow>
          ) : null}
          <div className={styles.list}>
            {section.entries.map((entry) => (
              <button
                key={entry.key}
                type="button"
                className={clsx(styles.item, entry.locked && styles.itemLocked)}
                onClick={() => navigate(entry.href)}
              >
                <span className={styles.itemLabel}>{entry.label}</span>
                {entry.key === 'tasks' && workspace.taskCount > 0 ? (
                  <span className={styles.itemState} aria-label={`${workspace.taskCount} offene Tasks`}>
                    {workspace.taskCount}
                  </span>
                ) : null}
                {entry.locked ? <span className={styles.itemState}>kein Zugriff</span> : null}
                <span className={styles.chevron} aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}

      <div className={styles.profile}>
        <Avatar
          initials={workspace.user.initials}
          name={name}
          size={36}
          src={workspace.user.avatarUrl ?? undefined}
        />
        <span className={styles.profileText}>
          <span className={styles.profileName}>{name}</span>
          <span className={styles.profileSub}>{workspace.organizationName}</span>
        </span>
        <span className={styles.profileActions}>
          {multiWorkspace ? (
            <TextLink as="button" size="small" onClick={actions.openWorkspaceSwitcher}>
              Workspace wechseln
            </TextLink>
          ) : null}
          <TextLink as="button" size="small" tone="muted" onClick={() => void actions.logout()}>
            Abmelden
          </TextLink>
        </span>
      </div>

      <div className={styles.note}>
        Tools ohne Recht stehen gesperrt in der Liste. Die Tabs unten sind für alle Rollen gleich.
      </div>
    </div>
  );
}
